import React, { memo, forwardRef, useCallback, useEffect, useMemo, useRef, useState } from "react";
import "./SimpleExcelViewer.css";
import { toColumnLabel } from "./utils/columnUtils";
import { withLiveChartData } from "./utils/chartLiveData";

import FormulaBar from "./components/FormulaBar";
import Grid from "./components/Grid";
import SheetTabs from "./components/SheetTabs";
import { HeaderContextMenu, FreezeContextMenu } from "./components/ContextMenus";
import CustomSortDialog from "./components/CustomSortDialog/CustomSortDialog";

import useWorkbookLoader from "./hooks/useWorkbookLoader";
import useSheetModel from "./hooks/useSheetModel";
import useCellSelection from "./hooks/useCellSelection";
import useFilters from "./hooks/useFilters";
import useHiddenRowsCols from "./hooks/useHiddenRowsCols";
import useResize from "./hooks/useResize";
import useFreezePanes from "./hooks/useFreezePanes";
import useScrollMemory from "./hooks/useScrollMemory";
import useRowWindow from "./hooks/useRowWindow";
import useColumnGrowth from "./hooks/useColumnGrowth";
import useRowGrowth from "./hooks/useRowGrowth";
import useSort from "./hooks/useSort";
import useMerge from "./hooks/useMerge";
import useKeyboardNav from "./hooks/useKeyboardNav";
import useClipboard from "./hooks/useClipboard";
import useHyperlinks from "./hooks/useHyperlinks";
import useHeaderContextMenu from "./hooks/useHeaderContextMenu";
import useZoom from "./hooks/useZoom";
import usePrint from "./hooks/usePrint";
import useCellEditing from "./hooks/useCellEditing";
import InCellEditor from "./components/Overlays/InCellEditor";
import CellHistoryDialog from "./components/CellHistoryDialog/CellHistoryDialog";
import AllChangesDialog from "./components/CellHistoryDialog/AllChangesDialog";

/**
 * SimpleExcelViewer — orchestrator.
 *
 * This component is intentionally thin: every piece of behaviour lives
 * in its own custom hook under ./hooks/* and every piece of UI in
 * ./components/*.  This file just wires them together.
 */
const SimpleExcelViewer = forwardRef(({
  documentId, pageNumber, openId, userId,
  fileId,                       // back-compat alias for documentId
  excelPayload,                 // { excelData (base64), fileType, fileName } from DocumentRPD
  height = "calc(87vh - 62px)",
  zoom = 1,
  zoomScale: zoomScaleProp,
  onLocked,
  onSaveSuccess,                // bubble save-success up to DocumentRPD's alert
  onDirtyChange,                // (bool) => void — unsaved-edit state for nav guards
  onWorkbookSaved,              // (base64Xlsx) => void — page caches the SAVED workbook
}, ref) => {
  const effectiveDocumentId = documentId || fileId;
  const componentRef = useRef(null);

  // ---- Workbook loading & sheet model ----
  // NOTE: The hook no longer issues its own /images/ request.  It consumes
  // the payload supplied by DocumentRPD's single fetch.
  const {
    loading, error, sheetNames, setSheetNames, sheetDataByName, setSheetDataByName,
    workbookBufferRef,
  } = useWorkbookLoader({ excelPayload, onLocked });

  const {
    activeSheet, setActiveSheet, prevSheetRef,
    activeModel, maxCols, colWidths,
    sheetImages, sheetCharts,
    // Raw (unsorted) fields — useSort produces the display versions below.
    normalizedRows: rawRows, rowHeights: rawHeights,
    cellStyles: rawStyles, cellRichHtml: rawRich,
    cellMerges: rawMerges, cellLinks: rawLinks,
  } = useSheetModel({ sheetNames, sheetDataByName });

  // ---- Column sorting (produces the display row data + keyed maps) ----
  const {
    sortLevels, getSortState, sortAsc, sortDesc, clearSort, applySort,
    hasHeaderRow,
    normalizedRows, rowHeights, cellStyles, cellRichHtml, cellLinks, cellMerges, formulas: displayFormulas,
    numFmts: displayNumFmts,
  } = useSort({
    activeSheet, prevSheetRef, activeModel,
    rawRows, rawHeights, rawStyles, rawRich, rawLinks, rawMerges,
  });

  const [customSortOpen, setCustomSortOpen] = useState(false);
  // { row, col } of the cell whose edit history is being viewed.
  const [historyTarget, setHistoryTarget] = useState(null);
  // Whole-workbook "All changes" dialog.
  const [allHistoryOpen, setAllHistoryOpen] = useState(false);


  // ---- Selection / filtering / hidden rows-cols ----
  const {
    selectedCell, setSelectedCell, setSelection,
    selectionType, setSelectionType, normalizedSelection,
    startSelection, extendSelection, selectColumn, selectRow,
    extendColumnSelection, extendRowSelection,
  } = useCellSelection({ activeSheet, normalizedRows, maxCols });

  // ---- Cell merging ----
  // The interactive Merge & Center toolbar is intentionally hidden, so the
  // merge handlers below are not consumed; useMerge now simply passes the
  // FILE's own merged cells through unchanged so they still render as-is.
  const {
    cellMerges: finalMerges, cellStyles: finalStyles,
  } = useMerge({
    activeSheet,
    baseMerges: cellMerges,
    baseStyles: cellStyles,
    selection: normalizedSelection,
  });

  const {
    hiddenCols, hiddenRows,
    handleHideCol, handleUnhideCols, handleHideRow, handleUnhideRows,
    isLastVisibleBeforeHidden, isLastVisibleBeforeHiddenRow,
  } = useHiddenRowsCols({ activeSheet, prevSheetRef });

  const {
    filters, activeFilterCol, setActiveFilterCol,
    toggleFilterMenu, applyFilter, isRowVisible,
    filterEnabled, isFilterColumn, toggleFilterEnabled,
  } = useFilters({
    activeSheet, activeModel, normalizedRows, hiddenRows,
    // Column selections scope the Filter toggle to just those columns.
    selectionType, normalizedSelection,
    // Signature of the current sort order — the filter row-snapshot is
    // recomputed when rows are reordered (but NOT on cell edits).
    sortSignature: JSON.stringify(sortLevels),
  });

  // Rename a sheet everywhere the display model knows it (names list, data
  // map, active tab). Readers may no longer rename sheets (QA: sheet names
  // must not be editable); this is kept only so useCellEditing can revert
  // any pending rename bookkeeping on Cancel.
  const applySheetRename = useCallback((oldName, newName) => {
    setSheetNames((prev) => prev.map((n) => (n === oldName ? newName : n)));
    setSheetDataByName((prev) => {
      if (!prev[oldName]) return prev;
      const next = {};
      // Preserve tab order while swapping the key.
      Object.keys(prev).forEach((k) => { next[k === oldName ? newName : k] = prev[k]; });
      return next;
    });
    setActiveSheet((prev) => (prev === oldName ? newName : prev));
  }, [setSheetNames, setSheetDataByName, setActiveSheet]);

  // ---- In-place cell editing (opt-in per reader email via AppContext) ----
  const {
    canEdit: editingAllowed, canSave: editingCanSave, editMode, toggleEditMode,
    commitCell, commitCells,
    save: saveEdits, dirty: editsDirty,
    saving: editsSaving, statusMsg: editStatusMsg,
    historyEnabled, fetchHistory,
    cancelEdits, saveSuccess, clearSaveSuccess,
  } = useCellEditing({
    userId,
    documentId: effectiveDocumentId,
    openId,
    fileName: excelPayload?.fileName,
    fileType: excelPayload?.fileType,
    activeSheet, sheetDataByName, setSheetDataByName, workbookBufferRef,
    sortActive: sortLevels.length > 0,
    filterEnabled,
    applySheetRename,
    onWorkbookSaved,
  });

  // Surface the unsaved-edit state to the parent (page-navigation guard) and
  // warn on tab close while edits are pending.
  useEffect(() => {
    if (typeof onDirtyChange === "function") onDirtyChange(!!editsDirty);
  }, [editsDirty, onDirtyChange]);
  useEffect(() => {
    if (!editsDirty) return undefined;
    const onBeforeUnload = (e) => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [editsDirty]);
  // Bubble a successful save up to DocumentRPD so its top-level Alert shows
  // the toast on the MAIN page (not clipped inside the viewer). Clear the
  // local flag immediately - the parent owns the visible alert now.
  useEffect(() => {
    if (saveSuccess) {
      if (typeof onSaveSuccess === "function") onSaveSuccess("Data saved successfully");
      clearSaveSuccess();
    }
  }, [saveSuccess, onSaveSuccess, clearSaveSuccess]);


  // Column options for the Custom Sort dialog — both the letter and the
  // first-row header value, so the dialog can label by whichever the
  // "My data has headers" checkbox selects.
  const sortColumns = useMemo(() => {
    const n = activeModel?.dataColCount || maxCols;
    const header = normalizedRows[0];
    return Array.from({ length: n }, (_, c) => {
      const h = header && header[c] != null && header[c] !== "" ? String(header[c]) : null;
      return { value: c, letter: toColumnLabel(c), header: h };
    });
  }, [activeModel?.dataColCount, maxCols, normalizedRows]);

  // ---- Resize + zoom ----
  const { startResize } = useResize({ activeSheet, colWidths, rowHeights, setSheetDataByName });
  const zoomScale = useZoom(zoomScaleProp, zoom);

  // ---- Freeze panes ----
  const {
    frozenRows, frozenCols, handleFreeze, handleUnfreeze,
    freezeCtx, setFreezeCtx, handleCellContextMenu,
    gridWrapperRef, theadRef,
    measuredTheadHeight, frozenRowOffsets, frozenColOffsets,
  } = useFreezePanes({
    activeSheet, sheetDataByName,
    rowHeights, colWidths,
    hiddenRows, hiddenCols, isRowVisible,
    normalizedRowsLength: normalizedRows.length,
    zoomScale,
  });

  // ---- Endless grid: grow rendered columns/rows on scroll (like Excel) ----
  const displayCols = useColumnGrowth({ gridWrapperRef, baseCols: maxCols, colWidths, activeSheet });
  const displayRows = useRowGrowth({ gridWrapperRef, baseRows: normalizedRows.length, rowHeights, activeSheet });

  // ---- Scroll memory + keyboard nav + hyperlinks ----
  const { pendingLinkScrollRef } = useScrollMemory({ gridWrapperRef, activeSheet });

  // ---- Row virtualization (only the visible slice is mounted) ----
  const rowWindow = useRowWindow({
    gridWrapperRef,
    rowCount: displayRows,
    rowHeights,
    isRowVisible,
    frozenRows,
    cellMerges: finalMerges,
    activeSheet,
  });

  useKeyboardNav({
    activeModel, normalizedRows, maxCols: displayCols, colWidths, rowHeights,
    gridWrapperRef, selectedCell, setSelectedCell, setSelection, setSelectionType,
  });

  // ---- Charts / images: selection, copy & paste (Edit mode) ----
  // Pasted copies are view-side objects appended after the file's own
  // objects (a removed paste leaves a null slot so indexes — and the
  // per-object move/resize state keyed by index — stay stable).
  const [pastedObjects, setPastedObjects] = useState({}); // { [sheet]: { charts: [], images: [] } }
  const [selectedObject, setSelectedObject] = useState(null); // { sheet, key, geom }
  const objectClipRef = useRef(null); // { kind, item, geom }
  const pastedForSheet = pastedObjects[activeSheet] || null;
  const allImages = useMemo(
    () => (pastedForSheet && pastedForSheet.images.length ? sheetImages.concat(pastedForSheet.images) : sheetImages),
    [sheetImages, pastedForSheet]
  );
  const allCharts = useMemo(
    () => (pastedForSheet && pastedForSheet.charts.length ? sheetCharts.concat(pastedForSheet.charts) : sheetCharts),
    [sheetCharts, pastedForSheet]
  );
  const selectedObjectKey = editMode && selectedObject && selectedObject.sheet === activeSheet
    ? selectedObject.key : null;

  const handleSelectObject = useCallback((key, geom) => {
    setSelectedObject({ sheet: activeSheet, key, geom });
  }, [activeSheet]);
  // Leaving Edit mode drops the object selection.
  useEffect(() => { if (!editMode) setSelectedObject(null); }, [editMode]);
  // Clicking a cell deselects the object (Excel behaviour).
  const handleStartSelection = useCallback((row, col, event) => {
    setSelectedObject(null);
    startSelection(row, col, event);
  }, [startSelection]);

  const getSelectedObject = useCallback(() => selectedObjectKey, [selectedObjectKey]);
  const copyObject = useCallback(() => {
    if (!selectedObjectKey || !selectedObject) return false;
    const [kind, idxStr] = selectedObjectKey.split(":");
    const idx = parseInt(idxStr, 10);
    const item = kind === "chart" ? allCharts[idx] : allImages[idx];
    if (!item) return false;
    objectClipRef.current = { kind, item, geom: selectedObject.geom };
    return true;
  }, [selectedObjectKey, selectedObject, allCharts, allImages]);
  const pasteObject = useCallback(() => {
    const clip = objectClipRef.current;
    if (!clip || !activeSheet) return;
    const { kind, item, geom } = clip;
    const w = Math.max(20, Math.round(geom && geom.width ? geom.width : (item.width || 400)));
    const h = Math.max(20, Math.round(geom && geom.height ? geom.height : (item.height || 300)));
    const EMU = 9525; // EMU per CSS px
    // Anchored at the active cell, same size as the copied object.
    const range = {
      tl: { row: selectedCell.row, col: selectedCell.col, rowOff: 0, colOff: 0 },
      ext: { cx: w * EMU, cy: h * EMU },
    };
    const copy = kind === "chart"
      ? { ...item, id: `${item.id || "chart"}-copy-${Date.now()}`, range, pasted: true }
      : { ...item, range, width: w, height: h, pasted: true };
    const cur = pastedObjects[activeSheet] || { charts: [], images: [] };
    const listName = kind === "chart" ? "charts" : "images";
    const base = kind === "chart" ? sheetCharts.length : sheetImages.length;
    const newKey = `${kind}:${base + cur[listName].length}`;
    setPastedObjects((prev) => {
      const p = prev[activeSheet] || { charts: [], images: [] };
      return { ...prev, [activeSheet]: { ...p, [listName]: p[listName].concat([copy]) } };
    });
    // Select the pasted copy, like Excel.
    setSelectedObject({ sheet: activeSheet, key: newKey, geom: { width: w, height: h } });
  }, [activeSheet, pastedObjects, selectedCell.row, selectedCell.col, sheetCharts.length, sheetImages.length]);

  // While an object is selected, keys act on the OBJECT (not the active
  // cell): Delete removes a pasted copy, Escape deselects, and typing /
  // Enter / F2 must not start editing the cell underneath. Capture phase so
  // this runs before the in-cell editor's window listener.
  useEffect(() => {
    if (!selectedObjectKey) return undefined;
    const onKey = (e) => {
      if (e.ctrlKey || e.metaKey || e.altKey) return; // Ctrl+C / Ctrl+V pass through
      const tag = String((e.target && e.target.tagName) || "").toLowerCase();
      if (tag === "input" || tag === "textarea") return;
      if (e.key === "Escape") { setSelectedObject(null); return; }
      if (e.key === "Delete" || e.key === "Backspace") {
        e.preventDefault();
        e.stopPropagation();
        const [kind, idxStr] = selectedObjectKey.split(":");
        const base = kind === "chart" ? sheetCharts.length : sheetImages.length;
        const pi = parseInt(idxStr, 10) - base;
        if (pi >= 0) {
          setPastedObjects((prev) => {
            const cur = prev[activeSheet];
            if (!cur) return prev;
            const listName = kind === "chart" ? "charts" : "images";
            const list = cur[listName].slice();
            list[pi] = null; // keep later indexes stable
            return { ...prev, [activeSheet]: { ...cur, [listName]: list } };
          });
          setSelectedObject(null);
        }
        return;
      }
      if (e.key.length === 1 || e.key === "Enter" || e.key === "F2") {
        e.preventDefault();
        e.stopPropagation();
      }
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [selectedObjectKey, activeSheet, sheetCharts.length, sheetImages.length]);

  // ---- Copy / paste (Edit mode only) ----
  // Gated on the ExcelWebEdit capability AND Edit mode: RPDs are
  // copy-protected documents, so outside Edit mode Ctrl+C keeps the
  // document-protection behaviour (no copy, no copy marker).
  const { copyRange, clearCopyRange } = useClipboard({
    enabled: !!(editingAllowed && editMode),
    activeSheet,
    selectedCell,
    normalizedSelection,
    normalizedRows,
    commitCells,
    // Paste selects the pasted range (Excel behaviour).
    selectionType, setSelection, setSelectedCell, setSelectionType,
    // Formatting copied with the values.
    cellStyles: finalStyles,
    cellRichHtml,
    dateFormats: activeModel ? activeModel.dateFormats : null,
    numFmts: displayNumFmts,
    // Charts / images.
    getSelectedObject, copyObject, pasteObject,
  });

  // Typing into / clearing a cell ends the copy marker, as in Excel (a paste
  // keeps it, so the same range can be pasted again).
  const commitTypedCell = useCallback((row, col, text) => {
    clearCopyRange();
    commitCell(row, col, text);
  }, [clearCopyRange, commitCell]);

  // Delete / Backspace in Edit mode: clear every NON-EMPTY cell of the
  // selection in one batch (one undo step). Whole-row / whole-column
  // selections are limited to the cells that actually hold data.
  const deleteSelection = useCallback(() => {
    clearCopyRange();
    const sel = normalizedSelection;
    const r2 = Math.min(sel.r2, normalizedRows.length - 1);
    const edits = [];
    for (let r = sel.r1; r <= r2; r += 1) {
      const row = normalizedRows[r];
      if (!row) continue;
      const c2 = Math.min(sel.c2, row.length - 1);
      for (let c = sel.c1; c <= c2; c += 1) {
        const v = row[c];
        const hasFormula = displayFormulas && displayFormulas[`${r}:${c}`];
        if ((v !== "" && v != null) || hasFormula) edits.push({ row: r, col: c, text: "" });
      }
    }
    if (edits.length) commitCells(edits);
  }, [clearCopyRange, normalizedSelection, normalizedRows, displayFormulas, commitCells]);

  // ---- Charts re-resolved against LIVE sheet data ----
  // Parsed charts carry the file's cached series values; after an edit the
  // cache is stale. Re-resolve each series' source range against the current
  // model so graphs mirror the reader's changes.
  const liveCharts = useMemo(
    () => withLiveChartData(allCharts, sheetDataByName),
    [allCharts, sheetDataByName]
  );

  const { handleLinkClick } = useHyperlinks({
    sheetNames, activeSheet, setActiveSheet, sheetDataByName,
    colWidths, rowHeights, gridWrapperRef,
    setSelectedCell, setSelection, pendingLinkScrollRef,
  });

  // ---- Header context menu ----
  const { headerCtx, handleHeaderContextMenu, closeHeaderContextMenu } = useHeaderContextMenu();

  // Reset header context menu when active sheet changes.
  useEffect(() => { closeHeaderContextMenu(); }, [activeSheet, closeHeaderContextMenu]);

  // ---- Imperative print API ----
  usePrint({ ref, workbookBufferRef, componentRef });

  // ---- Derived UI strings (formula bar) ----
  const selectionSummary = useMemo(() => {
    if (selectionType === "col") {
      const c1 = normalizedSelection.c1;
      const c2 = normalizedSelection.c2;
      return c1 === c2 ? `${toColumnLabel(c1)}:${toColumnLabel(c1)}` : `${toColumnLabel(c1)}:${toColumnLabel(c2)}`;
    }
    if (selectionType === "row") {
      const r1 = normalizedSelection.r1 + 1;
      const r2 = normalizedSelection.r2 + 1;
      return r1 === r2 ? `${r1}:${r1}` : `${r1}:${r2}`;
    }
    const rowsCount = normalizedSelection.r2 - normalizedSelection.r1 + 1;
    const colsCount = normalizedSelection.c2 - normalizedSelection.c1 + 1;
    return rowsCount === 1 && colsCount === 1 ? "" : `${rowsCount}R x ${colsCount}C`;
  }, [normalizedSelection, selectionType]);

  const selectedCellLabel = useMemo(() => {
    if (selectionType === "col") {
      const c1 = normalizedSelection.c1;
      const c2 = normalizedSelection.c2;
      return c1 === c2 ? `${toColumnLabel(c1)}:${toColumnLabel(c1)}` : `${toColumnLabel(c1)}:${toColumnLabel(c2)}`;
    }
    if (selectionType === "row") {
      const r1 = normalizedSelection.r1 + 1;
      const r2 = normalizedSelection.r2 + 1;
      return r1 === r2 ? `${r1}:${r1}` : `${r1}:${r2}`;
    }
    return `${toColumnLabel(selectedCell.col)}${selectedCell.row + 1}`;
  }, [selectedCell.col, selectedCell.row, selectionType, normalizedSelection]);

  const selectedCellText = useMemo(() => {
    if (!activeModel) return "";
    const formula = displayFormulas?.[`${selectedCell.row}:${selectedCell.col}`];
    if (formula) return formula;
    const value = normalizedRows?.[selectedCell.row]?.[selectedCell.col];
    return value == null ? "" : String(value);
  }, [activeModel, displayFormulas, normalizedRows, selectedCell.col, selectedCell.row]);

  // ---- Early returns for loading / error / empty ----
  if (loading) return <div className="simple-excel-viewer-status" style={{ height }}>Loading Excel...</div>;
  if (error)   return <div className="simple-excel-viewer-status simple-excel-viewer-error" style={{ height }}>{error}</div>;
  if (sheetNames.length === 0) return <div className="simple-excel-viewer-status" style={{ height }}>No sheets found in this file.</div>;

  return (
    <div className="simple-excel-viewer" style={{ height }}>
      <FormulaBar
        selectedCellLabel={selectedCellLabel}
        selectionSummary={selectionSummary}
        selectedCellText={selectedCellText}
        filterEnabled={filterEnabled}
        onToggleFilter={toggleFilterEnabled}
        onCustomSort={() => setCustomSortOpen(true)}
        sortActive={sortLevels.length > 0}
        editing={editingAllowed ? {
          editMode,
          // Never disable Edit for sort/filter - instead entering edit mode
          // clears them (below) so display coords match the file, keeping
          // edits safe. editBlocked stays false so the icon is clickable.
          editBlocked: false,
          dirty: editsDirty,
          saving: editsSaving,
          statusMsg: editStatusMsg,
          canSave: editingCanSave,
          onToggleEdit: () => {
            if (!editMode) {
              // Clear any active sort/filter so the edit grid is 1:1 with
              // the underlying file rows before editing begins.
              if (sortLevels.length > 0) clearSort();
              if (filterEnabled) toggleFilterEnabled();
            }
            toggleEditMode();
          },
          onCancel: cancelEdits,
          onSave: saveEdits,
        } : null}
        onCommitAt={commitTypedCell}
        selectedRow={selectedCell.row}
        selectedCol={selectedCell.col}
        editTargetKey={`${activeSheet}|${selectedCell.row}|${selectedCell.col}`}
        onShowAllHistory={historyEnabled ? () => setAllHistoryOpen(true) : undefined}
      />

      <Grid
        wrapperRef={gridWrapperRef}
        theadRef={theadRef}
        tableRef={componentRef}
        zoomScale={zoomScale}
        maxCols={displayCols}
        colWidths={colWidths}
        hiddenCols={hiddenCols}
        hiddenRows={hiddenRows}
        frozenCols={frozenCols}
        frozenColOffsets={frozenColOffsets}
        selectionType={selectionType}
        normalizedSelection={normalizedSelection}
        filters={filters}
        activeFilterCol={activeFilterCol}
        normalizedRows={normalizedRows}
        onToggleFilter={toggleFilterMenu}
        onApplyFilter={applyFilter}
        onCloseFilter={() => setActiveFilterCol(null)}
        onSelectColumn={selectColumn}
        onExtendColumn={extendColumnSelection}
        onHeaderContextMenu={handleHeaderContextMenu}
        onStartResize={startResize}
        isLastVisibleBeforeHidden={isLastVisibleBeforeHidden}
        isLastVisibleBeforeHiddenRow={isLastVisibleBeforeHiddenRow}
        rowHeights={rowHeights}
        cellStyles={finalStyles}
        cellRichHtml={cellRichHtml}
        cellMerges={finalMerges}
        cellLinks={cellLinks}
        isRowVisible={isRowVisible}
        selectedCell={selectedCell}
        frozenRows={frozenRows}
        measuredTheadHeight={measuredTheadHeight}
        frozenRowOffsets={frozenRowOffsets}
        onSelectRow={selectRow}
        onExtendRow={extendRowSelection}
        onStartSelection={handleStartSelection}
        onExtendSelection={extendSelection}
        onLinkClick={handleLinkClick}
        onCellContextMenu={handleCellContextMenu}
        activeSheet={activeSheet}
        sheetImages={allImages}
        sheetCharts={liveCharts}
        objectsEditable={editMode}
        copyRange={copyRange && copyRange.sheet === activeSheet ? copyRange : null}
        selectedObjectKey={selectedObjectKey}
        onSelectObject={handleSelectObject}
        defaultFont={activeModel ? activeModel.defaultFont : null}
        numFmts={displayNumFmts}
        rowWindow={rowWindow}
        getSortState={getSortState}
        isFilterColumn={isFilterColumn}
        onSortAsc={sortAsc}
        onSortDesc={sortDesc}
        onClearSort={clearSort}
        onCustomSort={() => setCustomSortOpen(true)}
      />

      <InCellEditor
        active={editMode}
        gridWrapperRef={gridWrapperRef}
        selectedCell={selectedCell}
        rows={normalizedRows}
        formulas={displayFormulas}
        onCommit={commitTypedCell}
        onDeleteSelection={deleteSelection}
        onNavigate={(dRow, dCol) => {
          // Move the selection like Excel does after a commit.
          const row = Math.max(0, Math.min(normalizedRows.length - 1, selectedCell.row + dRow));
          const col = Math.max(0, Math.min(maxCols - 1, selectedCell.col + dCol));
          const cell = { row, col };
          setSelectedCell(cell);
          setSelection({ anchor: cell, focus: cell, dragging: false });
        }}
      />

      <SheetTabs
        sheetNames={sheetNames}
        activeSheet={activeSheet}
        onSelect={setActiveSheet}
      />

      {headerCtx && (
        <HeaderContextMenu
          type={headerCtx.type}
          index={headerCtx.index}
          position={{ x: headerCtx.x, y: headerCtx.y }}
          hiddenSet={headerCtx.type === "col" ? hiddenCols : hiddenRows}
          onHide={headerCtx.type === "col" ? handleHideCol : handleHideRow}
          onUnhide={headerCtx.type === "col" ? handleUnhideCols : handleUnhideRows}
          onClose={closeHeaderContextMenu}
        />
      )}

      <FreezeContextMenu
        ctx={freezeCtx}
        frozenRows={frozenRows}
        frozenCols={frozenCols}
        onFreeze={handleFreeze}
        onUnfreeze={handleUnfreeze}
        onClose={() => setFreezeCtx(null)}
        onShowHistory={historyEnabled ? (row, col) => {
          setFreezeCtx(null);
          setHistoryTarget({ row, col });
        } : undefined}
      />

      {historyEnabled && (
        <CellHistoryDialog
          target={historyTarget}
          sheet={activeSheet}
          fetchHistory={fetchHistory}
          onClose={() => setHistoryTarget(null)}
        />
      )}

      {historyEnabled && (
        <AllChangesDialog
          open={allHistoryOpen}
          fetchHistory={fetchHistory}
          onClose={() => setAllHistoryOpen(false)}
        />
      )}


      <CustomSortDialog
        open={customSortOpen}
        columns={sortColumns}
        initialLevels={sortLevels}
        initialHasHeaderRow={hasHeaderRow}
        onApply={(levels, header) => applySort(levels, header)}
        onClose={() => setCustomSortOpen(false)}
      />
    </div>
  );
});

export default memo(SimpleExcelViewer);
